import { supabaseAnonKey, supabaseUrl } from '@/lib/supabaseClient';
import { PROJECT_ID } from '@/lib/project';

export interface MediaItem {
  key: string;
  fileName: string;
  url: string;
  size: number;
  lastModified: string;
}

interface MediaResult {
  success: boolean;
  message?: string;
}

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'avif', 'svg', 'bmp'];

function authHeaders(extra?: Record<string, string>): Record<string, string> {
  return {
    apikey: supabaseAnonKey,
    Authorization: `Bearer ${supabaseAnonKey}`,
    ...extra,
  };
}

function parseJson(text: string): Record<string, unknown> | null {
  try {
    return JSON.parse(text) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * Upload a file to the S3 media bucket under <project_id>/original/.
 * The backend Lambda generates optimized variants asynchronously.
 */
export async function uploadOriginalMedia(file: File): Promise<MediaResult> {
  const fileName = `original/${Date.now()}-${file.name}`;
  const endpoint = `${supabaseUrl}/functions/v1/s3-upload?project_id=${encodeURIComponent(
    PROJECT_ID
  )}`;
  try {
    const res = await fetch(endpoint, {
      method: 'POST',
      headers: authHeaders({
        'x-project-id': PROJECT_ID,
        'x-file-name': fileName,
        'Content-Type': file.type || 'application/octet-stream',
      }),
      body: file,
    });
    const text = await res.text();
    const parsed = parseJson(text);
    if (!res.ok || parsed?.success === false) {
      return { success: false, message: (parsed?.message as string) || text || `HTTP ${res.status}` };
    }
    return { success: true };
  } catch (error) {
    return { success: false, message: (error as Error).message };
  }
}

/**
 * List every object stored under <project_id>/.
 */
export async function listProjectMedia(): Promise<MediaItem[]> {
  const endpoint = `${supabaseUrl}/functions/v1/s3-list?project_id=${encodeURIComponent(
    PROJECT_ID
  )}`;
  const res = await fetch(endpoint, { headers: authHeaders() });
  const text = await res.text();
  const parsed = parseJson(text);
  if (!res.ok) {
    throw new Error((parsed?.message as string) || text || `HTTP ${res.status}`);
  }
  const items = (parsed?.items as MediaItem[]) || [];
  return items;
}

/**
 * Delete a single object under <project_id>/.
 */
export async function deleteProjectMedia(key: string): Promise<MediaResult> {
  const endpoint = `${supabaseUrl}/functions/v1/S3-delete?project_id=${encodeURIComponent(
    PROJECT_ID
  )}`;
  try {
    const res = await fetch(endpoint, {
      method: 'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ project_id: PROJECT_ID, key }),
    });
    const text = await res.text();
    const parsed = parseJson(text);
    if (!res.ok || parsed?.success === false) {
      return { success: false, message: (parsed?.message as string) || text || `HTTP ${res.status}` };
    }
    return { success: true };
  } catch (error) {
    return { success: false, message: (error as Error).message };
  }
}

export function getFileExtension(name: string): string {
  const clean = name.split('?')[0];
  const parts = clean.split('.');
  return parts.length > 1 ? parts.pop()!.toLowerCase() : '';
}

export function isImageItem(item: MediaItem): boolean {
  return IMAGE_EXTENSIONS.includes(getFileExtension(item.fileName || item.key));
}

export function isOptimizedItem(item: MediaItem): boolean {
  return !item.key.includes('/original/');
}

export function formatFileSize(size: number): string {
  if (!size || size <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const index = Math.min(Math.floor(Math.log(size) / Math.log(1024)), units.length - 1);
  const value = size / Math.pow(1024, index);
  return `${value.toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
}

export function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return '-';
  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return '-';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}/${pad(date.getMonth() + 1)}/${pad(date.getDate())} ${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
}

export { IMAGE_EXTENSIONS };